<template>
  <div class="entitlement-feature-management">
    <p
      v-if="hasAssignedFeature"
      class="feature-description"
    >
      This
      <strong>{{ itemTypeName }}</strong>
      inherits the {{ isInheritedFromAccount ? 'default' : '' }}
      <strong>{{ inheritedFeatureName }}</strong>
      Subscription Package
      {{ isInheritedFromAccount ? 'set in the Subscription Policies for this' : 'from the' }}
      <strong>{{ inheritedFeatureSource }}</strong>.
      <br />
      <span>Changes must be made in the Feature Collection.</span>
    </p>
    <p
      v-else
      class="feature-description"
    >
      By default, this
      <strong>{{ itemTypeName }}</strong>
      inherits the {{ isInheritedFromAccount ? 'default' : '' }}
      <strong>{{ inheritedFeatureName }}</strong>
      Subscription Package
      {{ isInheritedFromAccount ? 'set in the Subscription Policies for this' : 'from the' }}
      <strong>{{ inheritedFeatureSource }}</strong>.
      You can override this selection by choosing a different package.
    </p>
    <div class="toggle-wrapper">
      <ToggleButton
        v-model="overrideInheritance"
        color="#2591CF"
        :sync="true"
        :labels="false"
        :disabled="hasAssignedFeature"
      />
      <label
        for="override-inheritance"
        class="toggle-label"
      >
        Override Inheritance
      </label>
    </div>
    <div
      v-if="entitlementsFeaturesOptions.length > 0"
      class="checks-wrapper"
      :class="{ disabled: !overrideInheritance }"
    >
      <div
        v-for="item in entitlementsFeaturesOptions"
        :id="'entitlementFeature_' + item.code"
        :key="item.code"
      >
        <MaterialRadio
          :label="item.name"
          :value="item.code"
          :formData="formData"
          name="entitlementFeature"
          className="radio-input"
          groupClass="radio-entitlement-feature"
          :isEntitlementFeature="true"
          :onChange="handleRadioChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  mapGetters,
  mapState,
} from 'vuex';
import MaterialRadio from './inputs/MaterialRadio.vue';

export default {
  name: 'EntitlementFeatureManagement',
  components: {
    MaterialRadio,
  },
  props: {
    entitlementFeature: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    onChange: {
      type: Function,
      required: true,
      default: () => {},
    },
    itemType: {
      type: String,
      default: '',
      required: true,
    },
    parentType: {
      type: String,
      default: '',
    },
    featureId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      overrideInheritance: false,
      defaultPackage: null,
      formData: {
        entitlementFeature: '',
      },
    };
  },
  computed: {
    ...mapState({
      entitlementsFeatures: (state) => state.accounts.entitlementsFeatures,
    }),
    ...mapGetters([
      'getCurrentAccountId',
      'getCurrentAccount',
    ]),
    entitlementsFeaturesOptions() {
      const features = this.entitlementsFeatures.map((item) => ({
        code: item.id,
        name: item.id === this.currentAccountEntitlementsFeature
          ? `${item.name} (Default Account Package)`
          : item.name,
      }));

      return [
        {
          code: '0',
          name: 'No Entitlement Required',
        },
        ...features,
      ];
    },
    currentAccountEntitlementsFeature() {
      return this.getCurrentAccount.defaultEntitlementFeatureId;
    },
    inheritedFeatureName() {
      if (this.isInheritedFromAccount) {
        const feature = this.entitlementsFeatures
          .find((item) => item.id === this.currentAccountEntitlementsFeature);

        if (feature && feature.name) {
          return feature.name;
        }

        return this.currentAccountEntitlementsFeature;
      }

      const feature = this.entitlementsFeatures
        .find((item) => item.id === this.entitlementFeature.sourceId);

      if (feature && feature.name) {
        return feature.name;
      }

      return this.entitlementFeature.sourceId || 'No Entitlement Required';
    },
    inheritedFeatureSource() {
      if (this.isInheritedFromAccount) {
        return 'Account';
      }

      const source = this.entitlementFeature.source === 'StandaloneEventGroup' ? 'Event Group' : this.entitlementFeature.source;

      return `${this.entitlementFeature.sourceTitle} ${source}`;
    },
    isInheritedFromAccount() {
      return this.entitlementFeature.source === 'Account' || this.itemType === 'StandaloneEvent' || this.itemType === 'NewLive';
    },
    itemTypeName() {
      if (this.itemType === 'StandaloneEvent') {
        return 'Event';
      }
      if (this.itemType === 'EventGroup') {
        return 'Event Group';
      }
      if (this.itemType === 'NewLive') {
        return 'Live';
      }
      if (this.itemType) {
        return this.itemType;
      }

      return 'Item';
    },
    hasAssignedFeature() {
      return !!this.featureId && this.itemType !== 'Extra' && this.itemType !== 'Trailer';
    },
  },
  watch: {
    overrideInheritance(newValue) {
      this.onChange({ key: 'isInherited', value: !newValue });
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.entitlementFeature.id === '_') {
        this.formData.entitlementFeature = this.currentAccountEntitlementsFeature;
      } else if (
        !this.entitlementFeature.id
        && this.entitlementFeature.sourceId
        && this.entitlementFeature.isInherited
      ) {
        this.formData.entitlementFeature = this.entitlementFeature.sourceId;
      } else {
        this.formData.entitlementFeature = this.entitlementFeature.id || '0';
      }
    });

    if (!this.hasAssignedFeature) {
      this.overrideInheritance = !this.entitlementFeature.isInherited;
    }
  },
  methods: {
    handleRadioChange(e) {
      this.formData.entitlementFeature = e.target.value;

      this.onChange({ key: 'id', value: e.target.value });
    },
  },
};
</script>

<style scoped>
.entitlement-feature-management {
  color: #fff;
  font-size: 16px;
  line-height: 20px;
  font-weight: 400;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.feature-description {
  margin-bottom: 0px;
  color: #fff;
  font-size: 16px;
  line-height: 20px;
  font-weight: 400;
}

.description strong {
  font-weight: bold;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
}

.toggle {
  appearance: none;
  width: 60px;
  height: 26px;
  background: #C4C4C4;
  border-radius: 13px;
  position: relative;
  outline: none;
  cursor: pointer;
  transition: background 0.3s;
  padding: 0px;
}

.toggle:checked {
  background: #2591CF;
}

.toggle:before {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  top: 3px;
  left: 3px;
  transition: transform 0.3s;
}

.toggle:checked:before {
  transform: translateX(34px);
}

.toggle-label {
  margin-left: 10px;
  font-size: 14px;
  cursor: pointer;
}

.checks-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 10px 0px;
  transition: opacity 0.3s, pointer-events 0.3s;
}

.checks-wrapper.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.option {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.radio {
  width: 20px;
  height: 20px;
  margin-right: 10px;
  cursor: pointer;
  background: transparent;
  accent-color: #2591CF; /* Цвет радиокнопки при выборе */
}

.label {
  font-size: 14px;
  cursor: pointer;
  color: #fff;
  transition: color 0.3s;
  margin-bottom: 0px;
}

.checks-wrapper:not(.disabled) .label {
  color: #fff; /* Цвет текста при включённом чекбоксе */
}
</style>
